import React from 'react';
import PageLayout from '../components/PageLayout';
import Seo from '../components/Seo';
import { Quotes } from 'phosphor-react';
import { Link } from 'react-router-dom';
import timGillenImage from '../assets/profile/tim-gillen-profile.jpg';

// Quote Block Component
const QuoteBlock = ({ children }) => (
  <div className="my-8 pl-6 border-l-4 border-terrapin-blue">
    <div className="flex items-start">
      <Quotes size={32} className="text-terrapin-blue mr-4 flex-shrink-0 mt-1" />
      <p className="text-xl text-gray-600 italic">{children}</p>
    </div>
  </div>
);

// Content Block Component
const ContentBlock = ({ children, className = '' }) => (
  <div className={`prose prose-lg max-w-none mb-8 ${className}`}>
    {children}
  </div>
);

const OurStory = () => {
  return (
    <>
    <Seo
      title="Our Story | Local IT in Northern Michigan Since 1990"
      description="Serving Northern Michigan since 1990, Terrapin Networks brings decades of local experience to managed IT and technology support for small business."
    />
    <PageLayout
      title="Who is Terrapin Networks?"
      description={
        <div className="max-w-3xl">
          <p className="text-xl italic">
            Established in 1990, Terrapin Networks is a full-service business technology company specializing 
            in the needs of small & medium-sized businesses in the Northern Michigan area.
          </p>
        </div>
      }
      headerStyle="default"
      contentWidth="default"
      contentClassName="bg-white rounded-lg shadow-sm p-8"
    >
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col md:flex-row gap-8 items-start mb-8">
          <div className="md:w-1/2">
            <ContentBlock className="mb-4">
              <p>
                Tim started Terrapin specifically to bring business-first technology support to small businesses 
                throughout Northern Michigan.
              </p>
            </ContentBlock>

            <QuoteBlock>
              I come from a long line of small business folks, back to both sets of 
              grandparents. In my early teens our family moved north and purchased a 
              marina at Glen Lake, and I eventually partnered with a brother in taking 
              over the business from our parents.
            </QuoteBlock>
            <p>
            His first computer system was purchased in 1985 for the business he had at the time, a tool 
            sales business. After a few years he had everything in his business tied up in the simple 
            computer system and hand-held device he used daily. It was the "IT", the business' technology, 
            and the entire business was pretty much laid up when it was down.
          </p>
          </div>
          
          <div className="md:w-1/2">
            <div className="relative h-full">
              <img 
                src={timGillenImage}
                alt="Tim Gillen, founder of Terrapin Networks" 
                className="rounded-lg shadow-lg w-full h-full object-cover"
              />
              <div className="absolute bottom-4 left-4 bg-white bg-opacity-90 p-3 rounded-lg">
                <p className="text-sm font-medium text-gray-800">Tim Gillen</p>
                <p className="text-xs text-gray-600">Founder, Terrapin Networks</p>
              </div>
            </div>
          </div>
        </div>

        <ContentBlock className="mt-0">
          
          <p className="text-center font-medium text-gray-700 my-12 text-3xl md:text-3xl">
            Like any IT system, at some point, it quit working.
          </p>
          <p>
            So Tim went to the local computer place and asked them to fix it. The hard drive had failed – that 
            happened a lot back in those days – and five days later he found himself still calling the computer 
            place and asking for a status update.
          </p>
          <br />
          <p>
            On day five, while begging to get some quicker attention, as his business by now was quite paralyzed, 
            he was told, "And this this I'll never forget," he says, "The man on the other end of the phone said, 
            'Buddy, we all got problems. We'll get to it!', and down went the phone."
          </p>
          <br />
          <p>
            Now they may have been pretty busy, I don't know. Maybe it wasn't really their fault. There were precious 
            few choices in the area at that time, and that may have contributed to their lack of urgency. But this much 
            was clear: They didn't get it. They weren't keyed in to what the impact was for me. At no point during our 
            conversations did I ever get the feeling they were "with me" in my distress.
          </p>
          <br />
          <p>
            They were technicians. Computer guys. Techs 'fix things' – they don't always see business like businessmen do: 
            the dollars that are at risk with payroll, competitors nipping at your heels, and promises that have been made 
            to your customers, staff, and financial backers.
          </p>
        </ContentBlock>

        <div className="my-12">
          <h2 className="text-2xl font-semibold text-terrapin-blue mb-4">And So It Began...</h2>
          <QuoteBlock>
            I saw that gap between business and technology as a real need. That led me to start Terrapin Networks in 1990 
            (originally as Terrapin Computers), with a constant emphasis on how technology can be a real engine for business success.
          </QuoteBlock>
        </div>

        <div className="my-12">
          <h2 className="text-2xl font-semibold text-terrapin-blue mb-4">MEDIA OUTREACH</h2>
          <ContentBlock>
            <p className="italic">
              Tim has been a frequent guest on WTCM Talk Radio 580 over the years, primarily on the Ron Jolly Show 
              and Afternoon Drive; he's also made many appearances on WPBN TV 7&4 in the "Tech Matters" segment 
              during the "7&4 News Today" morning show.
            </p>
            <br />
            <p className="italic">
              From 2018-2020, Tim and his daughter Ruthy co-hosted a <span className="text-terrapin-red">WTCM radio show and podcast</span> called 
              "The Team Nerd Tech Show", later called "The Terrapin Small Biz Connection", where they chatted about 
              current tech events, cybersec, and interviewed small biz leaders in northern Michigan. You can hear those 
              past podcasts on Apple Podcasts, Stitcher, and iHeart Radio by searching for "The Terrapin Small Biz Connection"
            </p>
            <br />
            <Link 
              to="/media" 
              className="inline-flex items-center text-terrapin-blue hover:text-terrapin-red transition-colors duration-200 text-lg"
            >
              See more of Tim's media appearances
              <svg className="ml-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </ContentBlock>
        </div>

        <div className="grid md:grid-cols-2 gap-12 mt-16">
          <div>
            <h2 className="text-2xl font-bold mb-4">Now, About YOUR Business...</h2>
            <p className="text-gray-600">
              Where do you want your business to be in the next three years? The next five years? 
              These are common questions any of us involved in running a company have on our radar. 
              They are the basis of any business technology decision; principle number one is that 
              all technology decisions are first and foremost business decisions.
            </p>
            <p className="text-gray-600 mt-4">
              The role of technology is to get your business up the road to those 1-, 3-, and 5-year goals, 
              driving efficiency and profits along the way. When properly thought out and implemented, 
              technology is a powerful engine for business success.
            </p>
          </div>
          <div>
            <h2 className="text-2xl font-bold mb-4">My Question to You...</h2>
            <p className="text-xl italic text-gray-700 mb-4">
              Are business principles driving your technology spend?
            </p>
            <p className="text-gray-600 mb-8">
              If not, likely you are unaware of where to start. Let's fix that!
            </p>
            <Link 
              to="/contact"
              className="inline-block bg-terrapin-red text-white px-8 py-4 rounded-lg font-semibold hover:bg-opacity-90 transition-all duration-200"
            >
              Get in Touch With Us Today!
            </Link>
          </div>
        </div>
      </div>
    </PageLayout>
    </>
  );
};

export default OurStory; 